import { replace } from "react-router-dom";

/**
 * Record URLs. Each takes the record's public key — its slug or business number — and encodes it
 * as one path segment (an invoice number may contain `/`). Where a call site only has the id, pass
 * the id: the route's loader redirects a GUID to the canonical URL, so the link still lands right.
 */
const seg = (key: string) => encodeURIComponent(key);

export const brandPath = (slug: string) => `/catalogue/brands/${seg(slug)}`;
export const categoryPath = (slug: string) => `/catalogue/categories/${seg(slug)}`;
export const productPath = (slug: string) => `/catalogue/products/${seg(slug)}`;
export const skuPath = (slug: string) => `/catalogue/skus/${seg(slug)}`;
export const inventoryPath = (skuSlug: string) => `/inventory/${seg(skuSlug)}`;
export const tierPath = (slug: string) => `/tiers/${seg(slug)}`;
export const orderPath = (orderNumber: string) => `/orders/${seg(orderNumber)}`;
/** A Super Admin's form for taking the order through every step up to `target` (`pages/orders/Complete.tsx`). */
export const completeStepsPath = (orderNumber: string, target: string) => `${orderPath(orderNumber)}/complete?to=${target}`;
export const invoicePath = (invoiceNumber: string) => `/invoicing/${seg(invoiceNumber)}`;
export const creditNotePath = (creditNoteNumber: string) => `/invoicing/credit-notes/${seg(creditNoteNumber)}`;
export const dispatchPath = (invoiceNumber: string) => `/dispatch/${seg(invoiceNumber)}`;
export const customerPath = (customerNumber: string) => `/customers/${seg(customerNumber)}`;
export const refundPath = (refundNumber: string) => `/finance/refunds/${seg(refundNumber)}`;
export const importBatchPath = (batchNumber: string) => `/imports/${seg(batchNumber)}`;
export const reapplyPreviewPath = (previewNumber: string) => `/reports/staleness/reapply/${seg(previewNumber)}`;
export const userPath = (userNumber: string) => `/users/${seg(userNumber)}`;
export const syncItemPath = (itemNumber: string) => `/integrations/tally/queue/${seg(itemNumber)}`;
export const bannerPath = (slug: string) => `/website/banners/${seg(slug)}`;
/** The stock upload awaiting its commit — a validation result cached in this tab, not a stored record. */
export const UPLOAD_PREVIEW_PATH = "/inventory/uploads/preview";

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** True when a route param is a legacy record id rather than a slug or business number. */
export const isGuid = (value: string | undefined): value is string => GUID.test(value ?? "");

/**
 * A loader's answer to a legacy GUID URL: the canonical URL for the same record, keeping whatever
 * follows the record segment (`/units`, `/print`) and the query string (`?act=`, `?confirm=`).
 */
export function redirectToCanonical(request: Request, guid: string, canonical: string): Response {
  const url = new URL(request.url);
  const at = url.pathname.indexOf(guid);
  const rest = at < 0 ? "" : url.pathname.slice(at + guid.length);
  // Old catalogue row links appended `?activeOnly=1` for the list's Back link; no detail screen
  // reads it, so a legacy URL that is being redirected anyway sheds it.
  url.searchParams.delete("activeOnly");
  // `replace`, not `redirect`: on a direct visit a redirect becomes a PUSH, and Back would land
  // on the GUID URL only to be sent forward again.
  return replace(`${canonical}${rest}${url.search}`);
}
